import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import AdHocCommands from "../../api/adHocCommands"
import XMPPError from "@xmpp/error"
import type { Element } from "@xmpp/xml"
import { DataFormsParser } from "../../lib/formParser"
import { XmppContext } from "../../stream/xmppClient"
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "../../reducers/hooks"
import { selectGpsConfig } from "../../reducers/gpsConfigSelectors"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { driversProfiles } from "../../reducers/profilesSliceSelectors"
import { Profile, upsertProfile } from "../../reducers/profilesSlice"
import { RootState } from "../../reducers/store"
import {
  fetchRoster,
  parseAvailableDriverShares,
  parseCurrentDriverShare,
  parseDriverAndMucJids,
} from "./utils"
import {
  emptyDriverFields,
  fullPhoneNumber,
  type DriverFieldValues,
} from "./DriverFields"
import { AddDriverStep } from "./AddDriverStep"
import { updateDriverProfile } from "../../reducers/chatsSliceThunks"
import {
  VEHICLE_OCCUPYING_GROUPS,
  DRIVER_SEATING_GROUPS,
  CAR_CLEARABLE_GROUPS,
} from "@/lib/vehicleChat"
import { Trash, Check, ChevronDown, Plus } from "lucide-react"
import { defaultFilter } from "cmdk"
import { cn, stripInvalidXMLChars } from "@/lib/utils"
import { extractVhostFromJid } from "../../jidUtils"
import { findProfile } from "../../reducers/profilesSliceSelectors"
import { languages } from "@/lib/languages"
import { chatNamePrefix } from "@/lib/plates"
import useAllowVehicleChange from "../../hooks/useAllowVehicleChange"
import type { Car } from "@/reducers/carsSlice"
import {
  VehicleName,
  hasSeveralBrands,
  isGoneFromGps,
  plateKeyOf,
} from "./VehicleName"
import { useTranslation } from "react-i18next"

type Share = {
  label: string
  value: string
}

type TmsCar = {
  id: string
  label: string
  provider?: string
  instanceHost?: string
}

// Empty car assignment, not a real car - what the backend stores for "no vehicle".
const NO_CAR = "__no_car__"
const NO_TMS_CAR = "__no_tms_car__"
const NO_TMS_TRAILER = "__no_tms_trailer__"

const isManualCarId = (carId: string | undefined) =>
  !!carId && carId.startsWith("manual")

// A car id is "{gps integration}:{device}". The device half is the provider's
// own and stays with the vehicle for good; the half in front is the row id of
// the account's GPS integration, and re-creating that integration republishes
// the whole fleet under a new one.
const deviceIdOf = (carId: string) => carId.split(":").slice(1).join(":")

const integrationIdOf = (carId: string) => Number(carId.split(":")[0])

// Items are keyed by car id so duplicate plates stay distinct, but cmdk scores
// the value too and a fuzzy "794" would hit ids like 378:97412. Search only
// what the row shows.
const searchPlateAndBrand = (
  _carId: string,
  search: string,
  keywords?: string[],
) => defaultFilter(keywords?.join(" ") ?? "", search)

// Whether the chat has a vehicle - decides map position/tacho. Form only picks
// from the catalog, never creates one.
type VehicleKind = "vehicle" | "no_vehicle"

// New chat: unknown until picked, force the choice. Existing chat: no car_id
// already means "no vehicle", so reflect that instead of asking again.
const vehicleKindOf = (
  carId: string | undefined,
  isEditMode: boolean,
): VehicleKind | null => {
  if (carId) return "vehicle"
  return isEditMode ? "no_vehicle" : null
}

const VEHICLE_KINDS = [
  {
    kind: "vehicle",
    title: "fleet.editCrew.vehicleKinds.vehicle.title",
    detail: "fleet.editCrew.vehicleKinds.vehicle.detail",
  },
  {
    kind: "no_vehicle",
    title: "fleet.editCrew.vehicleKinds.noVehicle.title",
    detail: "fleet.editCrew.vehicleKinds.noVehicle.detail",
  },
] as const satisfies readonly {
  kind: VehicleKind
  title: string
  detail: string
}[]

const parseTmsCars = (response: Element): TmsCar[] => {
  const command = response.getChild("command")
  const form = command && DataFormsParser.parse(command)
  const cars: TmsCar[] = []

  for (const item of form?.items ?? []) {
    const id = item.fields["tms_car_id"]?.values[0]
    if (!id) continue

    const label = item.fields["label"]?.values[0] || id
    const provider = item.fields["provider"]?.values[0]
    const instanceHost = item.fields["instance_host"]?.values[0]
    cars.push({
      id,
      label,
      ...(provider ? { provider } : {}),
      ...(instanceHost ? { instanceHost } : {}),
    })
  }

  return cars
}

// Trailer registrations from get_tms_trailers; the registration is both the
// id and the label (it is the stable key - TMS trailer row ids churn per
// subcontractor).
const parseTmsTrailers = (response: Element): TmsCar[] => {
  const command = response.getChild("command")
  const form = command && DataFormsParser.parse(command)
  const trailers: TmsCar[] = []

  for (const item of form?.items ?? []) {
    const registration = item.fields["tms_trailer_id"]?.values[0]
    if (!registration) continue

    const label = item.fields["label"]?.values[0] || registration
    const provider = item.fields["provider"]?.values[0]
    const instanceHost = item.fields["instance_host"]?.values[0]
    trailers.push({
      id: registration,
      label,
      ...(provider ? { provider } : {}),
      ...(instanceHost ? { instanceHost } : {}),
    })
  }

  return trailers
}

interface EditCrewModalProps {
  isOpen: boolean
  onClose: (event?: any) => void
  mucJid?: string
  currentDriverAJid?: string
  currentDriverBJid?: string
  currentName?: string
  currentCarId?: string
  currentTmsCarId?: string
  currentTmsTrailerId?: string
  group?: string
}

const getDriverDisplayName = (profile: Profile) => {
  if (profile.firstName && profile.lastName) {
    return `${profile.firstName} ${profile.lastName}`
  }
  return profile.name || profile.jid
}

const NO_SHARE_VALUE = "no-share"

// The add-driver step, shown in place of the crew form. Adding a driver is a
// detour from naming a chat, not a second dialog on top of it - stacked
// dialogs leave this one inert (aria-hidden, pointer-events: none).
type DriverStep = "crew" | "add-driver"

export const EditCrewModal: React.FC<EditCrewModalProps> = ({
  isOpen,
  onClose,
  mucJid,
  currentDriverAJid,
  currentDriverBJid,
  currentCarId,
  currentTmsCarId,
  currentTmsTrailerId,
  currentName,
  group,
}) => {
  const { t } = useTranslation()
  const isEditMode = !!mucJid
  const initialCarId = currentCarId ?? NO_CAR
  const initialVehicleKind = vehicleKindOf(currentCarId, isEditMode)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedCarId, setSelectedCarId] = useState<string>(initialCarId)
  const [vehicleKind, setVehicleKind] = useState<VehicleKind | null>(
    initialVehicleKind,
  )
  const [carPickedByUser, setCarPickedByUser] = useState(false)
  const [selectedTmsCarId, setSelectedTmsCarId] = useState<string>(
    currentTmsCarId || NO_TMS_CAR,
  )
  const [selectedTmsTrailerId, setSelectedTmsTrailerId] = useState<string>(
    currentTmsTrailerId || NO_TMS_TRAILER,
  )
  const [selectedDriverJid, setSelectedDriverJid] = useState<string>(
    currentDriverAJid ?? "",
  )
  const [isFetchingShares, setIsFetchingShares] = useState(false)
  const [isFetchingTmsCars, setIsFetchingTmsCars] = useState(false)
  const [isFetchingTmsTrailers, setIsFetchingTmsTrailers] = useState(false)
  const [availableShares, setAvailableShares] = useState<Share[] | undefined>(
    undefined,
  )
  const [tmsCars, setTmsCars] = useState<TmsCar[]>([])
  const [tmsTrailers, setTmsTrailers] = useState<TmsCar[]>([])
  const [selectedShare, setSelectedShare] = useState<string>(NO_SHARE_VALUE)
  const [carSelectorOpen, setCarSelectorOpen] = useState(false)
  const [tmsCarSelectorOpen, setTmsCarSelectorOpen] = useState(false)
  const [tmsTrailerSelectorOpen, setTmsTrailerSelectorOpen] = useState(false)
  const [driverSelectorOpen, setDriverSelectorOpen] = useState(false)
  const [preferredLanguage, setPreferredLanguage] = useState("pl")
  const [chatName, setChatName] = useState(currentName ?? "")
  // Once typed by the user, the name is theirs - suggestions stop overwriting it.
  const [nameEditedByUser, setNameEditedByUser] = useState(false)
  // The chat just created by this form, once pubsub echoes it back mid-submit -
  // without it, the new chat misreads as the driver already seated elsewhere.
  const [createdMucJid, setCreatedMucJid] = useState<string | undefined>()
  const [driverStep, setDriverStep] = useState<DriverStep>("crew")
  const [newDriver, setNewDriver] =
    useState<DriverFieldValues>(emptyDriverFields)
  const [isAddingDriver, setIsAddingDriver] = useState(false)

  const { client } = useContext(XmppContext)
  const dispatch = useAppDispatch()
  const store = useAppStore()

  const driverProfiles = useAppSelector(driversProfiles)
  const carsById = useAppSelector((state: RootState) => state.cars)
  const cars = useMemo(() => Object.values(carsById), [carsById])
  const accountChats = useAppSelector((state: RootState) => state.accountChats)
  const driverAProfile = useAppSelector(findProfile(currentDriverAJid))
  const driverBProfile = useAppSelector(findProfile(currentDriverBJid))

  // Current GPS device, for reverse lookup when carId is not set.
  const gpsConfig = useAppSelector(
    selectGpsConfig(isEditMode && mucJid ? mucJid : ""),
  )
  const currentGpsDevice = gpsConfig?.deviceId ?? null

  // Vehicle behind the device, by legacyId.
  const carByGpsDevice = useMemo(() => {
    if (!currentGpsDevice) return null
    return cars.find((car) => car.legacyId === currentGpsDevice) ?? null
  }, [currentGpsDevice, cars])

  // The vehicle this chat holds, as the catalog knows it. The stored id can
  // have gone stale on its integration half alone - the vehicle is still
  // listed, under a new prefix - so fall back to the device behind the colon.
  const carHeldByChat = useMemo(() => {
    if (!currentCarId) return null
    const exact = carsById[currentCarId]
    if (exact) return exact
    if (isManualCarId(currentCarId)) return null

    const deviceId = deviceIdOf(currentCarId)
    if (!deviceId) return null

    // One device can be listed under several integrations. They all describe
    // the same vehicle, so any of them names it; take the newest integration so
    // the pick is stable between renders.
    return (
      cars
        .filter((car) => car.legacyId === deviceId)
        .sort((a, b) => integrationIdOf(b.id) - integrationIdOf(a.id))[0] ??
      null
    )
  }, [currentCarId, carsById, cars])

  const selectedCar = useMemo(() => {
    if (selectedCarId === NO_CAR) return null
    if (selectedCarId === currentCarId) return carHeldByChat
    return cars.find((car) => car.id === selectedCarId) ?? null
  }, [selectedCarId, currentCarId, carHeldByChat, cars])

  const sortedDriverProfiles = useMemo(() => {
    const myVhost = client?.jid?.toString()
      ? extractVhostFromJid(client.jid.toString())
      : null

    return [...driverProfiles]
      .filter((profile) => {
        if (!myVhost) return true
        const profileVhost = extractVhostFromJid(profile.jid)
        return profileVhost === myVhost
      })
      .sort((a, b) => {
        const nameA =
          a.firstName && a.lastName
            ? `${a.firstName} ${a.lastName}`
            : a.name || a.jid
        const nameB =
          b.firstName && b.lastName
            ? `${b.firstName} ${b.lastName}`
            : b.name || b.jid
        return nameA.localeCompare(nameB)
      })
  }, [driverProfiles, client?.jid])

  // Default chat name: plates then driver, same order as the backend. Only a
  // suggestion - a name typed by hand always wins. A side number the chat's
  // name carries in front of these plates ("05 WM2456S ...") stays in front.
  const suggestedChatName = useMemo(() => {
    const plates = selectedCar ? selectedCar.plates || selectedCar.label : ""
    const driver = driverProfiles.find((p) => p.jid === selectedDriverJid)
    const driverName = driver ? getDriverDisplayName(driver) : ""
    const prefix = chatNamePrefix(currentName ?? "", plates)
    return [prefix, plates, driverName].filter(Boolean).join(" ")
  }, [selectedCar, driverProfiles, selectedDriverJid, currentName])

  // New chat: follows the suggestion until typed over. Existing chat: keeps
  // its name - renaming is the user's call.
  useEffect(() => {
    if (!isOpen || isEditMode || nameEditedByUser) return
    setChatName(suggestedChatName)
  }, [isOpen, isEditMode, nameEditedByUser, suggestedChatName])

  // Rotating the driver rebuilds "{plates} {driver}" from scratch, backend
  // does the same - a typed name can't be honoured here or box and chat would
  // disagree. Filling an empty seat isn't a rotation, so a typed name stands.
  useEffect(() => {
    if (!isOpen || !isEditMode) return
    if (!selectedDriverJid || selectedDriverJid === (currentDriverAJid ?? ""))
      return
    const isRotation = !!currentDriverAJid
    if (!isRotation && nameEditedByUser) return
    if (!suggestedChatName) return
    setChatName(suggestedChatName)
  }, [
    isOpen,
    isEditMode,
    nameEditedByUser,
    currentDriverAJid,
    selectedDriverJid,
    suggestedChatName,
  ])

  // Picking a vehicle, or saying there's none, renames the chat like a driver
  // pick does above - keeps the box in sync with what save actually sends.
  // Gated on carPickedByUser, not "changed from initial": that check goes
  // blind the moment "No vehicle" returns selectedCarId to its own initial value.
  useEffect(() => {
    if (!isOpen || !isEditMode) return
    if (!carPickedByUser) return
    if (nameEditedByUser) return
    if (!suggestedChatName) return
    setChatName(suggestedChatName)
  }, [isOpen, isEditMode, carPickedByUser, nameEditedByUser, suggestedChatName])

  // `manual:` vehicles are old-form labels, not real vehicles - hidden from the
  // list except the one this chat already carries.
  const sortedCars = useMemo(() => {
    return cars
      .filter((car) => !isManualCarId(car.id) || car.id === currentCarId)
      .sort(
        (a, b) =>
          plateKeyOf(a).localeCompare(plateKeyOf(b)) ||
          Number(isGoneFromGps(a)) - Number(isGoneFromGps(b)),
      )
  }, [cars, currentCarId])

  const showBrands = useMemo(() => hasSeveralBrands(sortedCars), [sortedCars])

  const renderVehicleName = (car: Car) => (
    <VehicleName car={car} brand={showBrands ? car.gpsBrand : undefined} />
  )

  // This chat, edited or just created - never counts as "elsewhere".
  const ownMucJid = mucJid ?? createdMucJid

  // Double crew keeps its vehicle exclusively - still refused here (CAR_CLEARABLE_GROUPS).
  const carsBlockedByDoubleCrew = useMemo(() => {
    const blocked = new Set<string>()
    for (const chat of Object.values(accountChats)) {
      if (!chat.carId || chat.jid === ownMucJid) continue
      if (
        VEHICLE_OCCUPYING_GROUPS.includes(chat.group) &&
        !CAR_CLEARABLE_GROUPS.includes(chat.group)
      ) {
        blocked.add(chat.carId)
      }
    }
    return blocked
  }, [accountChats, ownMucJid])

  // A driver chat's vehicle can still be picked - saving clears it from below,
  // named so the warning can say where it moves from.
  const chatNameByCarId = useMemo(() => {
    const byCarId = new Map<string, string>()
    for (const chat of Object.values(accountChats)) {
      if (!chat.carId || chat.jid === ownMucJid) continue
      if (!CAR_CLEARABLE_GROUPS.includes(chat.group)) continue
      byCarId.set(chat.carId, chat.name || chat.jid)
    }
    return byCarId
  }, [accountChats, ownMucJid])

  // Rotating a driver onto another truck is fine, but the old chat they leave
  // behind gets named next to the pick as a warning. Double crew excluded on
  // purpose - the backend never pulls a driver out of one.
  const chatNameByDriverJid = useMemo(() => {
    const byJid = new Map<string, string>()
    for (const chat of Object.values(accountChats)) {
      if (chat.jid === ownMucJid) continue
      if (!DRIVER_SEATING_GROUPS.includes(chat.group)) continue
      for (const jid of [chat.driverA, chat.driverB]) {
        if (jid && !byJid.has(jid)) byJid.set(jid, chat.name || chat.jid)
      }
    }
    return byJid
  }, [accountChats, ownMucJid])

  const selectedDriverCurrentChat = selectedDriverJid
    ? chatNameByDriverJid.get(selectedDriverJid)
    : undefined

  const allowVehicleChange = useAllowVehicleChange()

  // A tracked vehicle is picked once and can't move; an old-form manual one
  // stays editable. A stored id that names nothing in the catalog is no
  // tracked vehicle either - locking it would leave the chat no way to ever
  // get one, which is how a chat ends up with an empty, immovable field.
  // When `allow-vehicle-change` is enabled, the vehicle can always be changed.
  const isCarFieldLocked =
    isEditMode &&
    !!carHeldByChat &&
    !isManualCarId(carHeldByChat.id) &&
    !allowVehicleChange

  const isHeldCarMissing =
    isEditMode &&
    !!currentCarId &&
    !isManualCarId(currentCarId) &&
    !carHeldByChat

  // Reason Save is disabled, shown next to it so the two can't drift apart.
  const isBusy = isSubmitting || isFetchingShares || isFetchingTmsCars
  const saveBlockedReason = (() => {
    if (!isEditMode && !vehicleKind)
      return t("fleet.editCrew.sayWhetherVehicle")
    // Not "nothing picked" but "nothing named": a lost id is still sitting in
    // selectedCarId, and saving it back would only re-store the dead reference.
    if (vehicleKind === "vehicle" && !selectedCar)
      return t("fleet.editCrew.pickVehicleFirst")
    if (!selectedDriverJid) return t("fleet.editCrew.pickDriverFirst")
    return null
  })()

  const tmsCarOptions = useMemo(() => {
    const options = [...tmsCars].sort((a, b) => a.label.localeCompare(b.label))
    if (
      tmsCars.length > 0 &&
      currentTmsCarId &&
      !options.some((option) => option.id === currentTmsCarId)
    ) {
      options.unshift({ id: currentTmsCarId, label: currentTmsCarId })
    }
    return options
  }, [tmsCars, currentTmsCarId])

  const selectedTmsCar = useMemo(() => {
    if (selectedTmsCarId === NO_TMS_CAR) return null
    return tmsCarOptions.find((car) => car.id === selectedTmsCarId) ?? null
  }, [selectedTmsCarId, tmsCarOptions])

  const tmsTrailerOptions = useMemo(() => {
    const options = [...tmsTrailers].sort((a, b) =>
      a.label.localeCompare(b.label),
    )
    if (
      tmsTrailers.length > 0 &&
      currentTmsTrailerId &&
      !options.some((option) => option.id === currentTmsTrailerId)
    ) {
      options.unshift({ id: currentTmsTrailerId, label: currentTmsTrailerId })
    }
    return options
  }, [tmsTrailers, currentTmsTrailerId])

  const selectedTmsTrailer = useMemo(() => {
    if (selectedTmsTrailerId === NO_TMS_TRAILER) return null
    return (
      tmsTrailerOptions.find(
        (trailer) => trailer.id === selectedTmsTrailerId,
      ) ?? null
    )
  }, [selectedTmsTrailerId, tmsTrailerOptions])

  useEffect(() => {
    if (isOpen) {
      setSelectedCarId(initialCarId)
      setVehicleKind(initialVehicleKind)
      setCarPickedByUser(false)
      setSelectedTmsCarId(currentTmsCarId || NO_TMS_CAR)
      setSelectedTmsTrailerId(currentTmsTrailerId || NO_TMS_TRAILER)
      setSelectedDriverJid(currentDriverAJid ?? "")
      setCreatedMucJid(undefined)
      setChatName(currentName ?? "")
      setNameEditedByUser(false)
      setDriverStep("crew")
      setNewDriver(emptyDriverFields())
      if (!isEditMode) {
        setSelectedShare(NO_SHARE_VALUE)
      }
    }
  }, [
    initialCarId,
    initialVehicleKind,
    currentTmsCarId,
    currentTmsTrailerId,
    currentDriverAJid,
    currentName,
    isOpen,
    isEditMode,
  ])

  // The language comes with the driver's vCard, which can land after the form
  // is open - kept out of the reset above so a late vCard cannot undo work
  // already done here. It only seeds the field while the seat still holds the
  // driver the form opened with; a driver picked or added since brings their
  // own language.
  useEffect(() => {
    if (!isOpen) return
    if (selectedDriverJid !== (currentDriverAJid ?? "")) return
    setPreferredLanguage(driverAProfile?.preferredLanguage ?? "pl")
  }, [
    isOpen,
    selectedDriverJid,
    currentDriverAJid,
    driverAProfile?.preferredLanguage,
  ])

  // No carId but a matching GPS device: auto-pick that vehicle. Skips one
  // already held elsewhere - this pick is silent, no warning was shown for it.
  useEffect(() => {
    if (
      isOpen &&
      isEditMode &&
      !currentCarId &&
      carByGpsDevice &&
      !carsBlockedByDoubleCrew.has(carByGpsDevice.id) &&
      !chatNameByCarId.has(carByGpsDevice.id) &&
      selectedCarId === NO_CAR
    ) {
      setSelectedCarId(carByGpsDevice.id)
      setVehicleKind("vehicle")
    }
  }, [
    isOpen,
    isEditMode,
    currentCarId,
    carByGpsDevice,
    carsBlockedByDoubleCrew,
    chatNameByCarId,
    selectedCarId,
  ])

  useEffect(() => {
    const fetchTmsCars = async () => {
      if (!isOpen) return

      setIsFetchingTmsCars(true)
      try {
        const response = await AdHocCommands.getTmsCars(client)
        setTmsCars(parseTmsCars(response))
      } catch (error) {
        console.error("Failed to fetch TMS cars:", error)
        setTmsCars([])
      } finally {
        setIsFetchingTmsCars(false)
      }
    }

    fetchTmsCars()
  }, [client, isOpen])

  useEffect(() => {
    const fetchTmsTrailers = async () => {
      if (!isOpen) return

      setIsFetchingTmsTrailers(true)
      try {
        const response = await AdHocCommands.getTmsTrailers(client)
        setTmsTrailers(parseTmsTrailers(response))
      } catch (error) {
        console.error("Failed to fetch TMS trailers:", error)
        setTmsTrailers([])
      } finally {
        setIsFetchingTmsTrailers(false)
      }
    }

    fetchTmsTrailers()
  }, [client, isOpen])

  useEffect(() => {
    const fetchShares = async () => {
      if (isOpen) {
        setIsFetchingShares(true)
        try {
          const availableSharesResult =
            await AdHocCommands.getAvailableDriverShares({ client })
          const shares = parseAvailableDriverShares(availableSharesResult)
          setAvailableShares(shares as Share[])

          if (isEditMode && mucJid) {
            try {
              const currentShareResult = await AdHocCommands.getCurrentShareMuc(
                {
                  client,
                  mucJid,
                },
              )
              const currentShare = parseCurrentDriverShare(currentShareResult)
              setSelectedShare(currentShare || NO_SHARE_VALUE)
            } catch (error) {
              console.error("Failed to fetch current MUC share:", error)
            }
          }
        } catch (error) {
          console.error("Failed to fetch available shares:", error)
          setAvailableShares([])
        } finally {
          setIsFetchingShares(false)
        }
      }
    }
    fetchShares()
  }, [client, isOpen, isEditMode, mucJid])

  const applyGpsDevice = async (carId: string | undefined, jid: string) => {
    if (!carId || isManualCarId(carId)) {
      // Nothing to point the device at. Cleared only if the user actually said so.
      if (carPickedByUser) {
        await AdHocCommands.setDriverGPSDevice(client, jid, null)
      }
      return
    }

    // Device ID is the second half of the car_id ("4:some_device").
    const gpsDeviceFromCarId = carId.split(":")[1]
    if (gpsDeviceFromCarId) {
      await AdHocCommands.setDriverGPSDevice(client, jid, gpsDeviceFromCarId)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      if (!selectedDriverJid) {
        throw new Error(t("fleet.editCrew.selectDriverError"))
      }

      // "No vehicle" sends an empty assignment; picking nothing sends none, so
      // a plain driver rotation leaves the existing vehicle alone. Sending the
      // catalog's own id, not the stored one, repoints a chat whose id went
      // stale on its integration half - in passing, on whatever edit brought
      // the user here.
      const carId = selectedCar
        ? selectedCar.id
        : vehicleKind === "no_vehicle"
          ? ""
          : undefined

      // Step 1: Upsert MUC with car_id
      const response = await AdHocCommands.upsertMuc({
        client,
        ...(isEditMode && { mucJid }),
        name: chatName.trim() || suggestedChatName,
        driverJid: selectedDriverJid,
        carId,
        tmsCarId: selectedTmsCarId === NO_TMS_CAR ? "" : selectedTmsCarId,
        tmsTrailerId:
          selectedTmsTrailerId === NO_TMS_TRAILER ? "" : selectedTmsTrailerId,
      })

      // Get the mucJid from response (for new MUCs) or use existing one
      let targetMucJid = mucJid
      if (!isEditMode) {
        const command = response.getChild("command")
        const form = command && DataFormsParser.parse(command)
        targetMucJid = form?.fields?.["muc_jid"]?.values[0]
        setCreatedMucJid(targetMucJid)
      }

      // Step 2: Set GPS device based on car_id
      if (targetMucJid) {
        await applyGpsDevice(carId, targetMucJid)

        // Step 3: Share MUC if a share is selected
        if (selectedShare && selectedShare !== NO_SHARE_VALUE) {
          const shareResponse = await AdHocCommands.shareMuc(
            client,
            targetMucJid,
            selectedShare,
          )

          // Step 4: Set GPS device for shared MUC (same as source MUC)
          const shareCommand = shareResponse.getChild("command")
          const shareForm = shareCommand && DataFormsParser.parse(shareCommand)
          const sharedMucJid = shareForm?.fields?.["shared_muc_jid"]?.values[0]

          if (sharedMucJid) {
            await applyGpsDevice(carId, sharedMucJid)
          }
        }

        // Step 5: Set preferred language for driver
        if (selectedDriverJid) {
          await AdHocCommands.setPreferredLanguage({
            client,
            driverJid: selectedDriverJid,
            language: preferredLanguage,
          })
        }
      }

      handleClose()
    } catch (e) {
      const error = e instanceof XMPPError ? e.text : (e as Error).message

      window.appToaster.show({
        message: error,
        intent: "danger",
        timeout: 2000,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  const handleVehicleKindChange = (kind: VehicleKind) => {
    // Set even on a no-op click: "No vehicle" may already be the default, and
    // clicking it is how that becomes a deliberate confirmation.
    setCarPickedByUser(true)
    if (kind === vehicleKind) return
    setVehicleKind(kind)
    // "vehicle" only opens the picker - nothing's chosen till one is picked.
    setSelectedCarId(NO_CAR)
  }

  const handleCarValueChange = (carId: string) => {
    setCarPickedByUser(true)
    setSelectedCarId(carId)
  }

  const getTmsCarLabel = (car: TmsCar) =>
    car.instanceHost ? `${car.label} - ${car.instanceHost}` : car.label

  const getTmsCarDisplayLabel = () => {
    if (selectedTmsCarId === NO_TMS_CAR) {
      return t("fleet.editCrew.noTmsCar")
    }
    return selectedTmsCar ? getTmsCarLabel(selectedTmsCar) : selectedTmsCarId
  }

  const handleTmsCarSelect = (tmsCarId: string) => {
    setSelectedTmsCarId(tmsCarId)
    setTmsCarSelectorOpen(false)
  }

  const getTmsTrailerDisplayLabel = () => {
    if (selectedTmsTrailerId === NO_TMS_TRAILER) {
      return t("fleet.editCrew.noTmsTrailer")
    }
    return selectedTmsTrailer
      ? getTmsCarLabel(selectedTmsTrailer)
      : selectedTmsTrailerId
  }

  const handleTmsTrailerSelect = (tmsTrailerId: string) => {
    setSelectedTmsTrailerId(tmsTrailerId)
    setTmsTrailerSelectorOpen(false)
  }

  const handleCarSelect = (carId: string) => {
    handleCarValueChange(carId)
    setCarSelectorOpen(false)
  }

  const getDriverDisplayLabel = () => {
    if (!selectedDriverJid) {
      return t("fleet.editCrew.selectDriver")
    }
    const selectedDriver = sortedDriverProfiles.find(
      (p) => p.jid === selectedDriverJid,
    )
    return selectedDriver
      ? getDriverDisplayName(selectedDriver)
      : t("fleet.editCrew.selectDriver")
  }

  const getDriverBDisplayLabel = () => {
    if (!driverBProfile) {
      return currentDriverBJid ?? "—"
    }
    return getDriverDisplayName(driverBProfile)
  }

  const handleDriverSelect = (driverJid: string) => {
    setSelectedDriverJid(driverJid)
    setDriverSelectorOpen(false)
  }

  const handleAddDriverOpen = () => {
    setNewDriver(emptyDriverFields())
    setDriverStep("add-driver")
  }

  const handleExistingDriverPick = (driverJid: string) => {
    setSelectedDriverJid(driverJid)
    setNewDriver(emptyDriverFields())
    setDriverStep("crew")
  }

  // Same call Driver Management makes: a driver in the list, no chat room of
  // their own (skipMuc). The chat is the one this form is already naming.
  const handleAddDriverSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsAddingDriver(true)

    const phoneNumber = fullPhoneNumber(newDriver)
    let response: Element
    try {
      response = await AdHocCommands.addDriver({
        client,
        // A name is free text; one character a stanza cannot carry breaks
        // the stream for that user.
        firstName: stripInvalidXMLChars(newDriver.firstName),
        lastName: stripInvalidXMLChars(newDriver.lastName),
        phoneNumber,
        plates: "-",
        skipMuc: true,
      })
    } catch (e) {
      const error = e instanceof XMPPError ? e.text : (e as Error).message

      window.appToaster.show({
        message: error,
        intent: "danger",
        timeout: 2000,
      })
      setIsAddingDriver(false)
      return
    }

    // The driver exists from here on. Nothing below may send the user back to
    // a filled form - pressing Add again would make a second one.
    const { driverJID } = parseDriverAndMucJids(response)

    if (driverJID) {
      // Straight into the store, so the picker can name them before the vCard
      // arrives. The refresh below replaces this with what the server has.
      dispatch(
        upsertProfile({
          profile: {
            jid: driverJID,
            name: `${newDriver.firstName} ${newDriver.lastName}`.trim(),
            firstName: newDriver.firstName,
            lastName: newDriver.lastName,
            phone: phoneNumber,
            preferredLanguage: newDriver.preferredLanguage,
            role: "driver",
          },
        }),
      )
      setSelectedDriverJid(driverJID)
      // The picker's language field follows the driver, or Save would push
      // the crew default back over the language just chosen.
      setPreferredLanguage(newDriver.preferredLanguage)
    } else {
      window.appToaster.show({
        message: t("fleet.editCrew.driverAddedNotPicked"),
        intent: "warning",
        timeout: 4000,
      })
    }

    setNewDriver(emptyDriverFields())
    setDriverStep("crew")

    // Follow-ups. Best effort: the form already holds everything it needs to
    // show, and the driver is created either way.
    try {
      if (driverJID) {
        await AdHocCommands.setPreferredLanguage({
          client,
          driverJid: driverJID,
          language: newDriver.preferredLanguage,
        })
        await fetchRoster(client, store)
        await dispatch(updateDriverProfile(client, driverJID))
      }
    } catch (error) {
      console.error("Failed to finish setting up the new driver:", error)
    } finally {
      setIsAddingDriver(false)
    }
  }

  const shouldShowNameField = group !== "double_crew"
  // "A" only means something next to a "B" - every other chat has one driver.
  const driverALabel =
    group === "double_crew"
      ? t("fleet.editCrew.driverA")
      : t("fleet.editCrew.driver")
  // A driver_muc or a double crew keeps the driver it was built around, so
  // there is nothing to pick and nothing to add.
  const canPickDriver = group !== "driver_muc" && group !== "double_crew"

  const addDriverStep = (
    <AddDriverStep
      values={newDriver}
      onChange={setNewDriver}
      drivers={sortedDriverProfiles}
      isAdding={isAddingDriver}
      onPickExisting={handleExistingDriverPick}
      onBack={() => setDriverStep("crew")}
      onSubmit={handleAddDriverSubmit}
    />
  )

  const crewForm = (
    <>
      <DialogHeader>
        <DialogTitle className="text-lg font-semibold">
          {isEditMode
            ? group === "driver_muc"
              ? t("fleet.editCrew.titleEditRoom")
              : t("fleet.editCrew.titleEdit")
            : t("fleet.editCrew.titleAdd")}
        </DialogTitle>
        <DialogDescription>
          {isEditMode
            ? t("fleet.editCrew.descriptionEdit")
            : t("fleet.editCrew.descriptionAdd")}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("fleet.editCrew.vehicleSection")}
            </h3>
            <div role="radiogroup" aria-label={t("fleet.editCrew.vehicleKind")}>
              <div className="grid grid-cols-2 gap-2">
                {VEHICLE_KINDS.map(({ kind, title, detail }) => (
                  <button
                    key={kind}
                    type="button"
                    role="radio"
                    aria-checked={vehicleKind === kind}
                    aria-label={t(title)}
                    disabled={isBusy || isCarFieldLocked}
                    onClick={() => handleVehicleKindChange(kind)}
                    className={cn(
                      "rounded-md border p-3 text-left transition-colors",
                      "disabled:cursor-not-allowed disabled:opacity-60",
                      vehicleKind === kind
                        ? "border-blue-600 bg-blue-600/10"
                        : "hover:bg-accent",
                    )}
                  >
                    <div className="text-sm font-medium">{t(title)}</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {t(detail)}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            {isCarFieldLocked && (
              <p className="text-sm text-muted-foreground">
                {t("fleet.editCrew.vehicleLocked")}
              </p>
            )}
            {isHeldCarMissing && (
              <p
                role="status"
                className="text-sm text-amber-600 dark:text-amber-500"
              >
                {t("fleet.editCrew.heldVehicleMissing")}
              </p>
            )}
            {vehicleKind === "vehicle" && (
              <div className="space-y-2">
                <Label htmlFor="vehicle">{t("fleet.editCrew.vehicle")}</Label>
                {cars.length > 5 ? (
                  <Popover
                    open={carSelectorOpen}
                    onOpenChange={setCarSelectorOpen}
                    modal={true}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-label={t("fleet.editCrew.vehicle")}
                        aria-expanded={carSelectorOpen}
                        disabled={
                          isSubmitting || isFetchingShares || isCarFieldLocked
                        }
                        className="w-full justify-between font-normal"
                      >
                        {selectedCar ? (
                          renderVehicleName(selectedCar)
                        ) : (
                          <span className="truncate">
                            {t("fleet.editCrew.selectVehicle")}
                          </span>
                        )}
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      className="z-[9999] w-[var(--radix-popover-trigger-width)] p-0"
                      sideOffset={5}
                    >
                      <Command filter={searchPlateAndBrand}>
                        <CommandInput
                          placeholder={t("fleet.editCrew.searchVehicle")}
                        />
                        <CommandList className="max-h-[300px] overflow-y-auto">
                          <CommandEmpty>
                            {t("fleet.editCrew.noVehicleFound")}
                          </CommandEmpty>
                          <CommandGroup>
                            {sortedCars.map((car) => (
                              <CommandItem
                                key={car.id}
                                value={car.id}
                                keywords={[plateKeyOf(car), car.gpsBrand ?? ""]}
                                disabled={carsBlockedByDoubleCrew.has(car.id)}
                                onSelect={() => handleCarSelect(car.id)}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    selectedCar?.id === car.id
                                      ? "opacity-100"
                                      : "opacity-0",
                                  )}
                                />
                                {renderVehicleName(car)}
                                {chatNameByCarId.has(car.id) && (
                                  <span className="ml-auto min-w-0 truncate pl-2 text-xs text-muted-foreground">
                                    {t("fleet.editCrew.inChat", {
                                      chat: chatNameByCarId.get(car.id),
                                    })}
                                  </span>
                                )}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                ) : (
                  <Select
                    disabled={
                      isSubmitting || isFetchingShares || isCarFieldLocked
                    }
                    value={selectedCar?.id ?? selectedCarId}
                    onValueChange={handleCarValueChange}
                  >
                    <SelectTrigger
                      aria-label={t("fleet.editCrew.vehicle")}
                      className="w-full"
                    >
                      <SelectValue
                        placeholder={t("fleet.editCrew.selectVehicle")}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {sortedCars.map((car) => (
                        <SelectItem
                          key={car.id}
                          value={car.id}
                          disabled={carsBlockedByDoubleCrew.has(car.id)}
                        >
                          {renderVehicleName(car)}
                          {chatNameByCarId.has(car.id) &&
                            ` ${t("fleet.editCrew.inChatParens", {
                              chat: chatNameByCarId.get(car.id),
                            })}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {!!selectedCar?.id && chatNameByCarId.has(selectedCar.id) && (
                  <p
                    role="status"
                    className="text-sm text-amber-600 dark:text-amber-500"
                  >
                    {t("fleet.editCrew.vehicleAssigned", {
                      chat: chatNameByCarId.get(selectedCarId),
                    })}
                  </p>
                )}
              </div>
            )}

            {tmsCars.length > 0 && (
              <div className="space-y-2">
                <Label>{t("fleet.editCrew.tmsCar")}</Label>
                <Popover
                  open={tmsCarSelectorOpen}
                  onOpenChange={setTmsCarSelectorOpen}
                  modal={true}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-label={t("fleet.editCrew.tmsCar")}
                      aria-expanded={tmsCarSelectorOpen}
                      disabled={
                        isSubmitting || isFetchingShares || isFetchingTmsCars
                      }
                      className="w-full justify-between font-normal"
                    >
                      <span className="truncate">
                        {isFetchingTmsCars
                          ? t("fleet.editCrew.loadingTmsCars")
                          : getTmsCarDisplayLabel()}
                      </span>
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    className="z-[9999] w-[var(--radix-popover-trigger-width)] p-0"
                    sideOffset={5}
                  >
                    <Command>
                      <CommandInput
                        placeholder={t("fleet.editCrew.searchTmsCar")}
                      />
                      <CommandList className="max-h-[300px] overflow-y-auto">
                        <CommandEmpty>
                          {t("fleet.editCrew.noTmsCarFound")}
                        </CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value={t("fleet.editCrew.noTmsCar")}
                            onSelect={() => handleTmsCarSelect(NO_TMS_CAR)}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedTmsCarId === NO_TMS_CAR
                                  ? "opacity-100"
                                  : "opacity-0",
                              )}
                            />
                            {t("fleet.editCrew.noTmsCar")}
                          </CommandItem>
                          {tmsCarOptions.map((car) => (
                            <CommandItem
                              key={car.id}
                              value={`${car.label} ${car.id} ${
                                car.instanceHost ?? ""
                              }`}
                              onSelect={() => handleTmsCarSelect(car.id)}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  selectedTmsCarId === car.id
                                    ? "opacity-100"
                                    : "opacity-0",
                                )}
                              />
                              <span className="truncate">
                                {getTmsCarLabel(car)}
                              </span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            )}

            {tmsTrailers.length > 0 && (
              <div className="space-y-2">
                <Label>{t("fleet.editCrew.tmsTrailer")}</Label>
                <Popover
                  open={tmsTrailerSelectorOpen}
                  onOpenChange={setTmsTrailerSelectorOpen}
                  modal={true}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-label={t("fleet.editCrew.tmsTrailer")}
                      aria-expanded={tmsTrailerSelectorOpen}
                      disabled={
                        isSubmitting ||
                        isFetchingShares ||
                        isFetchingTmsTrailers
                      }
                      className="w-full justify-between font-normal"
                    >
                      <span className="truncate">
                        {isFetchingTmsTrailers
                          ? t("fleet.editCrew.loadingTmsTrailers")
                          : getTmsTrailerDisplayLabel()}
                      </span>
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    className="z-[9999] w-[var(--radix-popover-trigger-width)] p-0"
                    sideOffset={5}
                  >
                    <Command>
                      <CommandInput
                        placeholder={t("fleet.editCrew.searchTmsTrailer")}
                      />
                      <CommandList className="max-h-[300px] overflow-y-auto">
                        <CommandEmpty>
                          {t("fleet.editCrew.noTmsTrailerFound")}
                        </CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value={t("fleet.editCrew.noTmsTrailer")}
                            onSelect={() =>
                              handleTmsTrailerSelect(NO_TMS_TRAILER)
                            }
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedTmsTrailerId === NO_TMS_TRAILER
                                  ? "opacity-100"
                                  : "opacity-0",
                              )}
                            />
                            {t("fleet.editCrew.noTmsTrailer")}
                          </CommandItem>
                          {tmsTrailerOptions.map((car) => (
                            <CommandItem
                              key={car.id}
                              value={`${car.label} ${car.id} ${
                                car.instanceHost ?? ""
                              }`}
                              onSelect={() => handleTmsTrailerSelect(car.id)}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  selectedTmsTrailerId === car.id
                                    ? "opacity-100"
                                    : "opacity-0",
                                )}
                              />
                              <span className="truncate">
                                {getTmsCarLabel(car)}
                              </span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            )}
          </section>

          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("fleet.editCrew.driverSection")}
            </h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="driver">{driverALabel}</Label>
                {canPickDriver && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    // isAddingDriver outlives the step - the roster refresh
                    // runs on after it. Reopening mid-flight would show a form
                    // with every field disabled.
                    disabled={
                      isSubmitting || isFetchingShares || isAddingDriver
                    }
                    onClick={handleAddDriverOpen}
                    className="h-7 px-2 text-xs"
                  >
                    <Plus className="mr-1 h-3 w-3" />
                    {t("fleet.editCrew.newDriver")}
                  </Button>
                )}
              </div>
              {!canPickDriver ? (
                <Input
                  id="driver"
                  disabled
                  value={getDriverDisplayLabel()}
                  className="w-full"
                />
              ) : (
                <Popover
                  open={driverSelectorOpen}
                  onOpenChange={setDriverSelectorOpen}
                  modal={true}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-label={driverALabel}
                      aria-expanded={driverSelectorOpen}
                      disabled={isSubmitting || isFetchingShares}
                      className="w-full justify-between font-normal"
                    >
                      <span className="truncate">
                        {getDriverDisplayLabel()}
                      </span>
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    className="z-[9999] w-[var(--radix-popover-trigger-width)] p-0"
                    sideOffset={5}
                  >
                    <Command>
                      <CommandInput
                        placeholder={t("fleet.editCrew.searchDriver")}
                      />
                      <CommandList className="max-h-[300px] overflow-y-auto">
                        <CommandEmpty>
                          {t("fleet.editCrew.noDriverFound")}
                        </CommandEmpty>
                        <CommandGroup>
                          {sortedDriverProfiles.map((profile) => (
                            <CommandItem
                              key={profile.jid}
                              value={getDriverDisplayName(profile)}
                              onSelect={() => handleDriverSelect(profile.jid)}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  selectedDriverJid === profile.jid
                                    ? "opacity-100"
                                    : "opacity-0",
                                )}
                              />
                              <span className="truncate">
                                {getDriverDisplayName(profile)}
                              </span>
                              {chatNameByDriverJid.has(profile.jid) && (
                                <span className="ml-auto shrink-0 pl-2 text-xs text-muted-foreground">
                                  {t("fleet.editCrew.inChat", {
                                    chat: chatNameByDriverJid.get(profile.jid),
                                  })}
                                </span>
                              )}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}
              {selectedDriverCurrentChat && (
                <p
                  role="status"
                  className="text-sm text-amber-600 dark:text-amber-500"
                >
                  {t("fleet.editCrew.driverAssigned", {
                    driver: getDriverDisplayLabel(),
                    chat: selectedDriverCurrentChat,
                  })}
                </p>
              )}
            </div>
            {group === "double_crew" && (
              <div className="space-y-2">
                <Label htmlFor="driverB">{t("fleet.editCrew.driverB")}</Label>
                <Input
                  id="driverB"
                  disabled
                  value={getDriverBDisplayLabel()}
                  className="w-full"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>{t("fleet.form.preferredLanguage")}</Label>
              <Select
                disabled={isSubmitting || isFetchingShares}
                value={preferredLanguage}
                onValueChange={setPreferredLanguage}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t("fleet.form.selectLanguage")} />
                </SelectTrigger>
                <SelectContent>
                  {languages.map((language) => (
                    <SelectItem key={language.code} value={language.code}>
                      <span className="flex items-center gap-2">
                        <span>{language.flag}</span>
                        <span>{language.name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {t("fleet.editCrew.languageHint")}
              </p>
            </div>
          </section>

          {(shouldShowNameField ||
            (availableShares && availableShares.length > 0)) && (
            <section className="space-y-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t("fleet.editCrew.chatSection")}
              </h3>
              {shouldShowNameField && (
                <div className="space-y-2">
                  <Label htmlFor="carName">
                    {t("fleet.editCrew.chatName")}
                  </Label>
                  <Input
                    id="carName"
                    value={chatName}
                    onChange={(e) => {
                      setNameEditedByUser(true)
                      setChatName(e.target.value)
                    }}
                    disabled={isSubmitting}
                    className="w-full"
                    placeholder={suggestedChatName}
                    aria-describedby="carNameHint"
                  />
                  <p id="carNameHint" className="text-sm text-muted-foreground">
                    {t("fleet.editCrew.chatNameHint")}
                  </p>
                </div>
              )}

              {availableShares && availableShares.length > 0 && (
                <div className="space-y-2">
                  <Label>{t("fleet.editCrew.shareTo")}</Label>
                  <div className="flex items-center gap-2">
                    <Select
                      disabled={isSubmitting || isFetchingShares}
                      value={selectedShare}
                      onValueChange={setSelectedShare}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue
                          placeholder={t("fleet.form.selectCompany")}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NO_SHARE_VALUE}>
                          {t("fleet.form.selectCompany")}
                        </SelectItem>
                        {availableShares.map((share) => (
                          <SelectItem key={share.value} value={share.value}>
                            {share.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      disabled={
                        !selectedShare ||
                        selectedShare === NO_SHARE_VALUE ||
                        isFetchingShares
                      }
                      onClick={(e) => {
                        e.preventDefault()
                        setSelectedShare(NO_SHARE_VALUE)
                      }}
                      className="shrink-0"
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </section>
          )}
        </div>

        <DialogFooter>
          <div className="flex w-full items-center justify-end gap-3">
            {saveBlockedReason && !isBusy && (
              <span className="mr-auto text-sm text-muted-foreground">
                {saveBlockedReason}
              </span>
            )}
            <Button
              type="button"
              variant="outline"
              disabled={isSubmitting || isFetchingShares}
              onClick={handleClose}
            >
              {t("common.cancel")}
            </Button>
            <Button
              autoFocus
              type="submit"
              disabled={isBusy || !!saveBlockedReason}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSubmitting || isFetchingShares
                ? t("fleet.form.saving")
                : t("common.save")}
            </Button>
          </div>
        </DialogFooter>
      </form>
    </>
  )

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="max-h-[calc(100dvh-4rem)] grid-cols-[minmax(0,1fr)] overflow-y-auto font-normal sm:max-w-[480px]"
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          document.body.style.pointerEvents = ""
        }}
      >
        {driverStep === "add-driver" ? addDriverStep : crewForm}
      </DialogContent>
    </Dialog>
  )
}
